export interface RepairedMarkdown {
  text: string;
  /** The final text ended inside a code fence the author never closed. */
  unclosedFence: boolean;
}

const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})/;
const PARTIAL_FENCE = /^ {0,3}(`{1,2}|~{1,2})$/;

interface OpenFence {
  char: string;
  length: number;
}

function scanFences(lines: string[]): OpenFence | null {
  let open: OpenFence | null = null;
  for (const line of lines) {
    const match = FENCE_OPEN.exec(line);
    if (!match) continue;
    const marker = match[1];
    const char = marker[0];
    if (open === null) {
      if (char === '`' && line.slice(match[0].length).includes('`')) continue;
      open = { char, length: marker.length };
    } else if (
      char === open.char &&
      marker.length >= open.length &&
      line.slice(match[0].length).trim() === ''
    ) {
      open = null;
    }
  }
  return open;
}

function countUnescaped(line: string, token: string): number {
  let count = 0;
  for (let i = 0; i < line.length; ) {
    if (line[i] === '\\') {
      i += 2;
      continue;
    }
    if (line.startsWith(token, i)) {
      count += 1;
      i += token.length;
      continue;
    }
    i += 1;
  }
  return count;
}

function closeInlineMarks(line: string): string {
  let out = line;
  if (countUnescaped(out.replace(/``+/g, ''), '`') % 2 === 1) return `${out}\``;
  for (const mark of ['**', '__']) {
    if (countUnescaped(out, mark) % 2 === 1) out = `${out}${mark}`;
  }
  return out;
}

export function repairMarkdown(text: string, options: { streaming: boolean }): RepairedMarkdown {
  const lines = text.split('\n');

  if (options.streaming) {
    if (lines.length > 0 && PARTIAL_FENCE.test(lines[lines.length - 1])) lines.pop();
    const open = scanFences(lines);
    if (open === null && lines.length > 0) {
      lines[lines.length - 1] = closeInlineMarks(lines[lines.length - 1]);
    }
    return { text: lines.join('\n'), unclosedFence: false };
  }

  const open = scanFences(lines);
  if (open === null) return { text, unclosedFence: false };
  const closer = open.char.repeat(open.length);
  return { text: `${text.replace(/\n?$/, '\n')}${closer}`, unclosedFence: true };
}
